import ArrowIcon from '@/assets/icons/icon_arrow-down.svg'
import { forwardRef, useEffect, useId, useMemo, useRef, useState } from 'react'
import cls from './select.module.css'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export type UISelectProps = {
  id?: string
  label?: string
  hint?: string
  error?: string | boolean
  placeholder?: string
  options: SelectOption[]
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
  className?: string
  size?: 'large' | 'small'
}

const Select = forwardRef<HTMLDivElement, UISelectProps>(function Select(
  { id, label, hint, error, placeholder = 'Select option', options, value, onChange, disabled = false, className = '', size = 'large' },
  ref,
) {
  const internalId = useId()
  const selectId = id ?? internalId
  const hasError = Boolean(error)

  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const selectedOption = options?.find((opt) => opt.value === value)

  const rootClass = useMemo(() => {
    return [cls.root, cls[size], disabled ? cls.disabled : '', hasError ? cls.error : '', className].filter(Boolean).join(' ')
  }, [size, disabled, hasError, className])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleOptionClick = (option: SelectOption) => {
    if (!option.disabled && onChange) {
      onChange(option.value)
      setIsOpen(false)
      triggerRef.current?.focus()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        handleToggle()
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
    }
  }

  return (
    <div className={rootClass} ref={ref}>
      {label ? (
        <label htmlFor={selectId} className={cls.label}>
          {label}
        </label>
      ) : null}

      <div className={cls.fieldWrapper}>
        <button
          id={selectId}
          ref={triggerRef}
          type="button"
          className={cls.trigger}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={hasError || undefined}
          aria-describedby={hint ? `${selectId}-hint` : undefined}
        >
          <span className={cls.triggerText} data-placeholder={!selectedOption ? 'true' : 'false'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <img
            src={ArrowIcon}
            alt=""
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </button>

        {isOpen && (
          <div ref={dropdownRef} className={cls.dropdown}>
            <div className={cls.optionsList} tabIndex={-1}>
              {options?.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`${cls.option} ${
                    option.value === value ? cls.optionSelected : ''
                  } ${option.disabled ? cls.optionDisabled : ''}`}
                  onClick={() => handleOptionClick(option)}
                  disabled={option.disabled}
                  aria-selected={option.value === value}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {hint ? (
        <div id={`${selectId}-hint`} className={cls.hint}>
          {hint}
        </div>
      ) : null}
      {typeof error === 'string' ? <div className={cls.errorText}>{error}</div> : null}
    </div>
  )
})

export default Select
